<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRoute } from "vue-router";
import Sidebar from "../components/layout/Sidebar.vue";
import TopBar from "../components/layout/TopBar.vue";
import ProjectsPanel from "../components/panels/ProjectsPanel.vue";
import DocsPanel from "../components/panels/DocsPanel.vue";
import AddModal from "../components/ui/AddModal.vue";
import SettingsModal from "../components/ui/SettingsModal.vue";
import ShareModal from "../components/ui/ShareModal.vue";
import UploadMonitor from "../components/ui/UploadMonitor.vue";
import DocumentDetailsPanel from "../components/ui/DocumentDetailsPanel.vue";
import "./tokens.css";
import { useTheme } from "../composables/useTheme.js";
import { useAddModal } from "../composables/useAddModal.js";
import { useSettingsModal } from "../composables/useSettingsModal.js";
import { useShareModal } from "../composables/useShareModal.js";
import { useTopBar } from "../composables/useTopBar.js";
import { useProjectsPanel } from "../composables/useProjectsPanel.js";

useTheme();

const { visible: modalVisible, openDocument, openProject } = useAddModal();
const { visible: settingsVisible, close: closeSettings } = useSettingsModal();
const { visible: shareVisible, close: closeShare } = useShareModal();
const { set: setTopBar } = useTopBar();
const route = useRoute();

// Whether the secondary panel is expanded — a plain user preference toggled by
// the TopBar's toggle button, independent of navigation.
const panelOpen = ref(true);

// Keep project info flowing in as soon as we're on a project/document route
// (regardless of which panel is actually shown) so we know whether a
// v2-document belongs to a real project or a standalone/singleton upload.
const { currentProject: routeProject, activate: activateProjectData } = useProjectsPanel();
activateProjectData();

// Which panel belongs to the current route (null = no panel for this route).
// v2-docs and v2-projects have no entry — those routes' main content already
// IS the full docs/projects list, so a redundant toggle to show the same list
// again as a side panel doesn't add anything there. v2-project (a single
// project's own document list) is different — the panel there shows that
// project's docs alongside the grid, not a duplicate of it.
const PANEL_FOR_ROUTE = {
  "v2-project": "projects",
};

const panelForRoute = computed(() => {
  if (route.name === "v2-document") {
    // Standalone docs (singleton projects) belong in the Docs panel — their
    // "project" is just a one-document wrapper, not a real project to browse.
    return routeProject.value?.isSingleton ? "docs" : "projects";
  }
  return PANEL_FOR_ROUTE[route.name] ?? null;
});

// What's actually shown: the route's panel, but only while the user has it open.
const activePanel = computed(() => (panelOpen.value ? panelForRoute.value : null));

// Static titles per route — views that load dynamic content (e.g. project name)
// will override this in their own onMounted after their data loads.
// showShare is left unset here — it depends on the user's role/permissions
// for that specific project/document, which the view determines once loaded
// (see Project.vue/Document.vue), so it stays hidden rather than flashing on.
const ROUTE_TOPBAR = {
  "v2-home":     { title: "Home" },
  "v2-search":   null, // TopBar renders the search input itself for this route
  "v2-activity": { title: "Activity" },
  "v2-projects": { title: "Projects" },
  "v2-docs":     { title: "Docs", subtitle: "(No project assigned)" },
  "v2-project":  { title: "Project", showMore: true },
  "v2-document": { title: "Document", showDownload: true },
};

// Use a getter `() => route.name` so Vue 3 tracks the specific property,
// not the entire reactive route object (which doesn't reliably trigger deep).
// immediate: true handles initial load + back/forward navigation.
watch(
  () => route.name,
  (name) => {
    // Update the TopBar title. `null` means the route manages its own header.
    const cfg = ROUTE_TOPBAR[name];
    if (cfg !== null && cfg !== undefined) setTopBar(cfg);
  },
  { immediate: true }
);

function onToggleSidePanel() {
  panelOpen.value = !panelOpen.value;
}

// Plus button in the side nav: default to the tab matching where the user is.
// Project routes -> "Create Project"; everywhere else (incl. Docs) -> "Add document".
function onCreate() {
  const isProjectRoute = route.name === "v2-project" || route.name === "v2-projects";
  if (isProjectRoute) {
    openProject();
  } else {
    openDocument();
  }
}

onMounted(() => {
  document.querySelectorAll("#splash-screen").forEach((el) => el.remove());
});
</script>

<template>
  <div class="flex h-screen w-full overflow-hidden bg-slate-50 dark:bg-slate-900">
    <Sidebar @create="onCreate" />

    <!-- Secondary panel: width animates open/closed; inert blocks focus when hidden -->
    <div
      class="shrink-0 overflow-hidden dark:bg-slate-800"
      :style="{ width: activePanel ? '256px' : '0px', transition: 'width 0.2s ease' }"
      :inert="!activePanel"
    >
      <ProjectsPanel v-show="activePanel === 'projects'" :is-active="activePanel === 'projects'" @close="panelOpen = false" />
      <DocsPanel v-show="activePanel === 'docs'" :is-active="activePanel === 'docs'" @close="panelOpen = false" />
    </div>

    <div class="flex flex-1 flex-col overflow-hidden min-w-0 border-l border-gray-200 dark:border-slate-700">
      <TopBar
        :panel-open="panelOpen"
        :panel-available="!!panelForRoute"
        @toggle-panel="onToggleSidePanel"
      />
      <!-- Document view controls its own scroll via #viewerContainer; all other routes scroll normally -->
      <main :class="['flex-1', route.name === 'v2-document' ? 'overflow-hidden' : 'overflow-y-auto']">
        <router-view />
      </main>
    </div>

    <!-- Global add/create modal -->
    <AddModal v-if="modalVisible" />
    <!-- Global settings modal -->
    <SettingsModal v-if="settingsVisible" @close="closeSettings" />
    <!-- Global share modal -->
    <ShareModal v-if="shareVisible" @close="closeShare" />
    <!-- Global upload progress monitor -->
    <UploadMonitor />
    <!-- Global document details panel -->
    <DocumentDetailsPanel />
  </div>
</template>
